import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { MaterialAttribute, OntologySourceReference } from "@bioconnect-ui/curation-service";
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ControlledTermType, ControlledTerm, Origin } from './admin-service.types';
import { PagedResponse } from '@bioconnect-ui/shared-service';

export interface AdminConfig {
  restServer: string;
  wijmoLicenseKey: string;
}

@Injectable({
  providedIn: 'root',
})

export class AdminService {

  constructor(private httpClient: HttpClient,
    @Inject('adminServiceConfig') private adminServiceConfig: AdminConfig) { }

  private restServer = this.adminServiceConfig.restServer;
  /**
   * Get a list of all ontology source references
   */
  getOntologySources(): Observable<OntologySourceReference[]> {
    return this.httpClient
      .get<PagedResponse>(
        `${this.restServer}/ontology_service/ontology_source_reference/`
      ).pipe(map((result) => <OntologySourceReference[]>result.results));
  }
  /**
   * Get a list of all ontology types
   */
  getOntologyTypes(): Observable<ControlledTermType[]> {
    return this.httpClient
      .get<PagedResponse>(
        `${this.restServer}/ontology_service/controlled_term_type/`
      ).pipe(map((result) => <ControlledTermType[]>result.results));
  }

  /**
   * Get a list of all terms for a specific type, by ID
   */
  getTermsByTypeId(typeId: number, currentPage?: number, pageSize?: number): Observable<PagedResponse> {
    if (!currentPage) currentPage = 1;
    if (!pageSize) pageSize = 1000;
    return this.httpClient
      .get<PagedResponse>(
        `${this.restServer}/ontology_service/controlled_term/?type_id=${typeId}&page=${currentPage}&page_size=${pageSize}`
      ).pipe(map((result) => <PagedResponse>result));
  }

  /**
   * Batch update an array of field descriptors
   */
  updateTerms(terms: ControlledTerm[]) {
    return this.httpClient.patch(
      `${this.restServer}/ontology_service/controlled_term/batch_update/`,
      terms,
      { headers: { 'skip-error-interceptor': 'true' } }
    );
  }
  /**
   * Get a list of all characteristic categories
   */
  getCharacteristicCategories(): Observable<MaterialAttribute[]> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/material_attribute/?page_size=500`
    ).pipe(map((result) => <MaterialAttribute[]>result.results));
  }

  /**
   * Batch update an array of characteristic categories
   */
  updateCharacteristicCategories(categories: MaterialAttribute[]): Observable<MaterialAttribute[]> {
    return this.httpClient.patch<MaterialAttribute[]>(
      `${this.restServer}/metadata_repository/material_attribute/batch_update/`,
      categories,
      { headers: { 'skip-error-interceptor': 'true' } }
    );
  }

  /**
 * Get a list of all origins the current user has access to
 */
  getOriginList(filter: string, page: number, pageSize: number): Observable<PagedResponse> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/origin_registration/origin/?filter=${filter}&page=${page}&page_size=${pageSize} `
    );
  }

  getOrigins(): Observable<Origin[]> {
    return this.httpClient
      .get<PagedResponse>(
        `${this.restServer}/origin_registration/origin/?page_size=1000`
      ).pipe(map((result) => <Origin[]>result.results));
  }

  createNewOrigin(newOrigin: Origin): Observable<Origin> {
    return this.httpClient.post<Origin>(
      `${this.restServer}/origin_registration/origin/`,
      newOrigin,
      { headers: { 'skip-error-interceptor': 'true' } }
    );
  }

  updateOrigin(origin: Origin): Observable<Origin> {
    return this.httpClient.put<Origin>(
      `${this.restServer}/origin_registration/origin/${origin.id}/`,
      origin
    );
  }

  refresheOriginApiKey(origin: Origin): Observable<Origin> {
    return this.httpClient.post<Origin>(
      `${this.restServer}/origin_registration/origin/refresh_api_key/`,
      origin
    );
  }

  deleteOrigin(id: number) {
    return this.httpClient.delete(
      `${this.restServer}/origin_registration/origin/${id}/`
    );
  }
}
